import { Flex, Grid, Stack, TextInput, Textarea, Select } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { CenteredLoading } from 'components/CenteredLoading';
import { PaperHeader } from 'components/PaperHeader/PaperHeader';
import { PortalButton } from 'components/Button/PortalButton';
import { BsFloppy } from 'react-icons/bs';
import { useForm } from '@mantine/form';
import { completeCountryList, LanguageCode } from 'utils/i18n';
import { useParams } from 'react-router-dom';
import { FormColumn } from 'components/Form/FormColumn';
import { useCustomMutation } from 'hooks/api/useCustomMutation';
import { updateGroup } from 'services/groups';
import { queryKeys } from 'utils/query';
import { GroupDetail } from 'models/groups';
import { validateEmail, validateLength } from 'utils/validation';
import { useGroup } from 'hooks/api/useGroups';

export const GroupSettingsForm = () => {
  const { t } = useTranslation();
  const { groupId } = useParams<{ groupId: string; tab?: string }>();
  const { data, isLoading } = useGroup(groupId);
  const { language } = i18next;
  const countries = useMemo(() => completeCountryList(language), [language]);
  const { mutate: updateGroupDetails, isPending } = useCustomMutation({
    fn: updateGroup,
    onSuccess: {
      invalidateQueryKeys: [[queryKeys.groupData, groupId], [queryKeys.userData]],
      message: t('group_settings_successfully_updated'),
    },
  });

  const form = useForm<GroupDetail>({
    mode: 'uncontrolled',
    initialValues: data,
    validate: {
      name: (value) => validateLength({ value, length: 3, label: t('group_name_update_error') }),
      description: (value) => validateLength({ value, length: 3, label: t('group_description_update_error') }),
      patientConfiguration: {
        mainContactEmail: (value) => validateEmail(value),
      },
    },
  });

  if (isLoading) return <CenteredLoading />;

  return (
    <form onSubmit={form.onSubmit((values) => updateGroupDetails(values))}>
      <PaperHeader titleKey={'group_general_tab'} iconKey={'groupsettings_general_general_tooltip'} />
      <Stack mt={20}>
        <Grid>
          <FormColumn label={t('name_label')}>
            <TextInput {...form.getInputProps('name')} disabled={isPending} />
          </FormColumn>
          <FormColumn label={t('description_label')}>
            <Textarea {...form.getInputProps('description')} disabled={isPending} />
          </FormColumn>
          <FormColumn label={t('group_main_contact_email_label')}>
            <TextInput {...form.getInputProps('patientConfiguration.mainContactEmail')} disabled={isPending} />
          </FormColumn>
          <FormColumn label={t('group_language_label')}>
            <Select
              data={Object.values(LanguageCode)
                .filter((lang) => lang !== LanguageCode.American_English)
                .map((lang) => ({
                  value: lang,
                  label: t(`lang_${lang}`),
                }))}
              {...form.getInputProps('patientConfiguration.mainContactLanguage')}
              key={form.key('mainContactLanguage')}
              disabled={isPending}
            />
          </FormColumn>
          <FormColumn label={t('group_address_line1_label')}>
            <TextInput {...form.getInputProps('address.addressLine1')} disabled={isPending} />
          </FormColumn>
          <FormColumn label={t('group_address_line2_label')}>
            <TextInput {...form.getInputProps('address.addressLine2')} disabled={isPending} />
          </FormColumn>
          <FormColumn label={t('group_zip_label')}>
            <TextInput {...form.getInputProps('address.postalCode')} disabled={isPending} />
          </FormColumn>
          <FormColumn label={t('group_city_label')}>
            <TextInput {...form.getInputProps('address.city')} disabled={isPending} />
          </FormColumn>
          <FormColumn label={t('group_country_label')}>
            <Select
              key={form.key('address.country')}
              data={countries}
              placeholder={t('group_country_label')}
              disabled={isPending}
              {...form.getInputProps('address.country')}
            />
          </FormColumn>
        </Grid>
        <Flex justify={'flex-start'}>
          <PortalButton type={'submit'} label={t('save_label')} Icon={BsFloppy} loading={isPending} />
        </Flex>
      </Stack>
    </form>
  );
};
